import { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Plus, Eye, CalendarCheck, CreditCard, AlertCircle, Check, Copy } from 'lucide-react'
import type { Booking, Child } from '@/api/client'
import { fetchBookings, fetchChildren } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { YearSelector } from '@/components/ui/year-selector'
import { cn } from '@/lib/utils'

export function BookingsPage() {
  const { t, i18n } = useTranslation(['bookings', 'common', 'payments'])
  const { parent } = useAuthStore()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [children, setChildren] = useState<Child[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [activeTab, setActiveTab] = useState<'active' | 'cancelled'>('active')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const handleCopyCode = useCallback((code: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 1500)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const [bookingsData, childrenData] = await Promise.all([
          fetchBookings(),
          fetchChildren()
        ])
        setBookings(bookingsData || [])
        setChildren(childrenData || [])
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'bookings' }))
      } finally {
        setLoading(false)
      }
    }

    if (parent?.id) {
      fetchData()
    } else {
      setLoading(false)
    }
  }, [parent?.id, t])

  const getChildName = (childId: number) => {
    const child = children.find(c => c.id === childId)
    return child ? `${child.first_name} ${child.last_name}` : t('bookingsBox.unknownChild')
  }

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" => {
    switch (status) {
      case 'finalized':
      case 'confirmed':
        return 'success'
      case 'interested':
      case 'awaiting-communication':
      case 'info-verified':
        return 'warning'
      case 'detail_checking':
        return 'info'
      case 'cancelled':
        return 'destructive'
      case 'completed':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  // Available years from data
  const availableYears = useMemo(() => {
    return [...new Set(bookings.map(b => new Date(b.created_at).getFullYear()))].sort((a, b) => b - a)
  }, [bookings])

  // Filtered bookings by year and tab
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      const year = new Date(b.created_at).getFullYear()
      if (year !== selectedYear) return false
      return activeTab === 'cancelled' ? b.status === 'cancelled' : b.status !== 'cancelled'
    })
  }, [bookings, selectedYear, activeTab])

  // Count for tab badges
  const activeCount = useMemo(() => {
    return bookings.filter(b => {
      const year = new Date(b.created_at).getFullYear()
      return year === selectedYear && b.status !== 'cancelled'
    }).length
  }, [bookings, selectedYear])

  const cancelledCount = useMemo(() => {
    return bookings.filter(b => {
      const year = new Date(b.created_at).getFullYear()
      return year === selectedYear && b.status === 'cancelled'
    }).length
  }, [bookings, selectedYear])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('bookingsBox.title')}</h1>
        </div>
        <Button asChild>
          <Link to="/bookings/add">
            <Plus className="h-4 w-4 mr-1" />
            {t('bookingsBox.addBooking')}
          </Link>
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <YearSelector
          years={availableYears}
          selectedYear={selectedYear}
          onChange={setSelectedYear}
        />

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('active')}
            className={cn(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              activeTab === 'active'
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t('tabs.active', { ns: 'common' })}
            {activeCount > 0 && (
              <span className="ml-1.5 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                {activeCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('cancelled')}
            className={cn(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              activeTab === 'cancelled'
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t('tabs.cancelled', { ns: 'common' })}
            {cancelledCount > 0 && (
              <span className="ml-1.5 text-xs bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-full">
                {cancelledCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive font-medium">{error}</p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredBookings.length > 0 ? (
        <div className="space-y-3">
          {filteredBookings.map((booking) => (
            <Card key={booking.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground">{getChildName(booking.child)}</h3>
                      <Badge variant={getStatusVariant(booking.status)}>
                        {t(`bookingsBox.statuses.${booking.status}`)}
                      </Badge>
                      {booking.in_waiting_list && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {t('bookingsBox.waitingList')}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-muted-foreground">{t('bookingsBox.code')}:</span>{' '}
                        <Badge
                          variant="outline"
                          className="font-mono cursor-pointer hover:bg-muted transition-colors gap-1.5"
                          onClick={() => handleCopyCode(booking.code)}
                        >
                          {booking.code}
                          {copiedCode === booking.code ? (
                            <Check className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('bookingsBox.event')}:</span>{' '}
                        <span className="font-medium">{booking.event_name || t('bookingsBox.unknownEvent')}</span>
                      </div>
                      {booking.social_security_fund && (
                        <div>
                          <span className="text-muted-foreground">{t('bookingsBox.socialSecurityRegistry')}:</span>{' '}
                          <span className="font-medium">
                            {booking.social_security_fund.name}
                            {booking.social_security_sub_fund && (
                              <> / {booking.social_security_sub_fund.name}</>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/bookings/${booking.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        {t('view', { ns: 'common' })}
                      </Link>
                    </Button>
                    {booking.is_payment_eligible && (
                      <Button size="sm" asChild>
                        <Link to={`/payments/checkout?booking=${booking.id}`}>
                          <CreditCard className="h-3.5 w-3.5 mr-1" />
                          {t('bookingsBox.payNow')}
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>

                {booking.payments_info && booking.payments_info.length > 0 && (
                  <div className="pt-0.5 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{t('bookingsBox.relatedPayments')}:</p>
                    {booking.payments_info.map((payment) => (
                      <div key={payment.id} className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-md px-2.5 py-1.5">
                        <CreditCard className="h-3 w-3 flex-shrink-0" />
                        <span className="font-mono">{payment.merchant_reference}</span>
                        <Badge variant="secondary" className="text-[10px] px-1 py-0">
                          {payment.is_early_bird && payment.payment_type === 'full'
                            ? t('types.earlyBirdFull', { ns: 'payments' })
                            : payment.is_early_bird && payment.payment_type === 'advance'
                              ? t('types.regularAdvance', { ns: 'payments' })
                              : t(`types.${payment.payment_type}`, { ns: 'payments' })}
                        </Badge>
                        <span className="font-medium">
                          {new Intl.NumberFormat(i18n.language === 'el' ? 'el-GR' : 'en-US', { style: 'currency', currency: 'EUR' }).format(parseFloat(payment.amount))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <CalendarCheck className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">
              {activeTab === 'cancelled'
                ? t('bookingsBox.noCancelledBookings', { defaultValue: t('bookingsBox.noBookings') })
                : t('bookingsBox.noBookings')
              }
            </p>
            {activeTab === 'active' && (
              <Button asChild>
                <Link to="/bookings/add">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('bookingsBox.addBooking')}
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
